import React, { useState, useRef, useEffect } from 'react';
import Lottie from 'lottie-react';

/**
 * Notification icon with Lottie animation that plays on hover
 */
const NotificationLottieIcon = ({ notification, size = 32, isParentHovered = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const lottieRef = useRef(null);
  
  // Use parent hover state if provided, otherwise use local hover
  const shouldPlay = isParentHovered || isHovered;

  const [animationData, setAnimationData] = useState(null);

  // Get animation path based on notification type
  const getAnimationPath = () => {
    switch (notification.type) {
      case 'new_message':
        return '/notfications/Chat V2.json';
      case 'order_placed':
      case 'order_paid':  // Seller receives new order
      case 'order_accepted':
      case 'order_completed':
        return '/notfications/Order.json';
      case 'seller_approved':
        return '/notfications/Versus.json';
      case 'booster_approved':
      case 'booster_rejected':
      case 'new_boosting_request':
      case 'new_boosting_offer':
        // For boosting notifications, show game icon if available in metadata
        if (notification.metadata?.game) {
          // Return null to show game icon instead
          return null;
        }
        return '/notfications/Versus.json';
      default:
        return null;
    }
  };

  const animationPath = getAnimationPath();

  // Load animation data from public folder
  useEffect(() => {
    if (animationPath) {
      fetch(animationPath)
        .then(res => res.json())
        .then(data => setAnimationData(data))
        .catch(err => {
          console.error('Error loading Lottie animation:', err);
          setAnimationData(null);
        });
    } else {
      setAnimationData(null);
    }
  }, [animationPath]);

  // Play animation on hover (either parent or local)
  useEffect(() => {
    if (shouldPlay && lottieRef.current && animationData) {
      // Reset to beginning and play
      lottieRef.current.goToAndStop(0);
      lottieRef.current.play();
    } else if (!shouldPlay && lottieRef.current) {
      // Stop and reset when not hovered
      lottieRef.current.stop();
      lottieRef.current.goToAndStop(0);
    }
  }, [shouldPlay, animationData]);

  // Get game icon path helper
  const getGameIconPath = (gameName) => {
    const iconMap = {
      'Fortnite': 'Fortnite.png',
      'Counter Strike 2': 'CS2.png',
      'Roblox': 'Roblox.png',
      'Valorant': 'Valorant.png',
      'Warcraft': 'Worldofwarcraft.png',
      'Clash Royale': 'Clashroyale.png',
      'Apex Legends': 'ApexLegends.png',
      'Arc Raiders': 'ARcraiders.png',
      'COD Call of Duty': 'Callofduty.png',
      'Call of Duty': 'Callofduty.png',
      'EA Sports FC': 'EAsports.png',
      'Escape from Tarkov': 'Escapefromtarkov.png',
      'League of Legends': 'Leagueoflegends.png',
      'Old School RuneScape': 'Oldschoolrunescape.png',
      'Rainbow Six Siege': 'Rainbowsix.png',
      'Rocket League': 'rocketleague.png'
    };
    
    // Try exact match first
    if (iconMap[gameName]) {
      return `/main/hero section/promos/navbaricons/${iconMap[gameName]}`;
    }
    
    // Try lowercase match
    const lowerGameName = gameName.toLowerCase();
    for (const [key, value] of Object.entries(iconMap)) {
      if (key.toLowerCase() === lowerGameName) {
        return `/main/hero section/promos/navbaricons/${value}`;
      }
    }
    
    // Special cases for games with icons in different locations
    if (gameName === '8 Ball Pool') {
      return '/main/games/icons/8bp.webp';
    }
    if (gameName === 'Kaiju No. 8') {
      return '/main/games/icons/kaiju8.jpg';
    }
    if (gameName === '99 Nights in the Forest') {
      return '/main/games/icons/99nitf.webp';
    }
    if (gameName === 'Adopt Me') {
      return '/main/games/icons/adoptme.jpeg';
    }
    if (gameName === 'Albion Online') {
      return '/main/games/icons/alibiononline.webp';
    }
    if (gameName === 'All Star Tower Defense X') {
      return '/main/games/icons/allstartowerdefense.jpeg';
    }
    if (gameName === 'Animal Crossing: New Horizons') {
      return '/main/games/icons/animalcrossing.jpg';
    }
    if (gameName === 'Anime Defenders') {
      return '/main/games/icons/animedefenders.webp';
    }
    if (gameName === 'Anime Vanguards') {
      return '/main/games/icons/animevanguards.jpg';
    }
    if (gameName === 'ARK: Survival Ascended') {
      return '/main/games/icons/arksurvivalascended.webp';
    }
    if (gameName === 'Arknights') {
      return '/main/games/icons/Arknights.png';
    }
    if (gameName === 'Ayakashi Rumble!') {
      return '/main/games/icons/AyakashiRumble.webp';
    }
    if (gameName === 'Battlenet') {
      return '/main/games/icons/battlenet.jpg';
    }
    if (gameName === 'Arena Breakout') {
      return '/main/games/icons/arenabreakout1.jpg';
    }
    if (gameName === 'Arena Breakout: Infinite') {
      return '/main/games/icons/arenabreakout.jpg';
    }
    if (gameName === 'Black Clover M') {
      return '/main/games/icons/Blackclover.jpeg';
    }
    if (gameName === 'Black Desert Online') {
      return '/main/games/icons/blackdessertonline.png';
    }
    if (gameName === 'Bleach: Brave Souls') {
      return '/main/games/icons/Bleachbravesouls.webp';
    }
    if (gameName === 'Blood Strike') {
      return '/main/games/icons/bloodstrike.webp';
    }
    if (gameName === 'Blox Fruits') {
      return '/main/games/icons/bloxfruits.jpg';
    }
    if (gameName === 'Brawl Stars') {
      return '/main/games/icons/brawl-stars.webp';
    }
    if (gameName === 'Call of Duty Mobile') {
      return '/main/games/icons/codmobile.webp';
    }
    if (gameName === 'Car Parking Multiplayer') {
      return '/main/games/icons/carparkingmultiplayer.webp';
    }
    if (gameName === 'Chaos Zero Nightmare') {
      return '/main/games/icons/choaszero.webp';
    }
    if (gameName === 'Chocotto Land') {
      return '/main/games/icons/chocottoland.jpg';
    }
    if (gameName === 'Clash of Clans') {
      return '/main/games/icons/clashofclans.jpg';
    }
    if (gameName === 'Cookie Run: Kingdom') {
      return '/main/games/icons/cookierunkingdom.webp';
    }
    if (gameName === 'Crunchyroll') {
      return '/main/games/icons/crunchyroll.jpg';
    }
    if (gameName === 'Dark and Darker') {
      return '/main/games/icons/darkanddarker.jpg';
    }
    if (gameName === 'DarkOrbit') {
      return '/main/games/icons/darkorbit.jpg';
    }
    if (gameName === 'DayZ') {
      return '/main/games/icons/dayz.avif';
    }
    if (gameName === 'Dead By Daylight') {
      return '/main/games/icons/deadbydaylight.jpg';
    }
    if (gameName === 'Dead Rails') {
      return '/main/games/icons/deadrails.jpg';
    }
    if (gameName === 'Delta Force') {
      return '/main/games/icons/deltaforce.jpeg';
    }
    if (gameName === 'Destiny 2') {
      return '/main/games/icons/destiny2.jpeg';
    }
    if (gameName === 'Diablo 4') {
      return '/main/games/icons/diablo4.avif';
    }
    if (gameName === 'Diablo Immortal') {
      return '/main/games/icons/diabloimortal.jpg';
    }
    if (gameName === 'Digimon Super Rumble') {
      return '/main/games/icons/digimonsuperrumble.jpg';
    }
    if (gameName === 'Discord') {
      return '/main/games/icons/discord.jpg';
    }
    if (gameName === 'Dota 2') {
      return '/main/games/icons/dota2.jpg';
    }
    if (gameName === 'Dragon Ball Legends') {
      return '/main/games/icons/dragonballlegends.jpg';
    }
    if (gameName === 'Dragon Ball Z Dokkan Battle') {
      return '/main/games/icons/DragonBallZDokkanBattle.jpg';
    }
    if (gameName === 'Duet Night Abyss') {
      return '/main/games/icons/duetnightabyss.jpg';
    }
    if (gameName === 'EA Sports FC Mobile') {
      return '/main/games/icons/eafcmobile.jpg';
    }
    if (gameName === 'eFootball') {
      return '/main/games/icons/efotball.jpg';
    }
    if (gameName === 'Elden Ring') {
      return '/main/games/icons/eldenring.jpg';
    }
    if (gameName === 'Elder Scrolls Online') {
      return '/main/games/icons/elderscrollsonline.jpg';
    }
    if (gameName === 'Epic Games') {
      return '/main/games/icons/epicgames.jpg';
    }
    if (gameName === 'Epic Seven') {
      return '/main/games/icons/epicseven.jpg';
    }
    if (gameName === 'Etheria: Restart') {
      return '/main/games/icons/etheriarestart.jpg';
    }
    if (gameName === 'Fallout 76') {
      return '/main/games/icons/fallout76.jpg';
    }
    if (gameName === 'Fate/Grand Order') {
      return '/main/games/icons/fategrandorder.jpg';
    }
    if (gameName === 'Final Fantasy XIV') {
      return '/main/games/icons/finalfantasyxix.jpg';
    }
    if (gameName === 'Fisch') {
      return '/main/games/icons/fisch.jpg';
    }
    if (gameName === 'Forza Horizon 5') {
      return '/main/games/icons/forzahorizon5.jpg';
    }
    if (gameName === 'Garena Free Fire') {
      return '/main/games/icons/garenafreefire.jpeg';
    }
    if (gameName === 'Genshin Impact') {
      return '/main/games/icons/genshinimpact.avif';
    }
    if (gameName === 'Girls Frontline 2: Exilium') {
      return '/main/games/icons/girlsfrontline2exilium.jpg';
    }
    if (gameName === 'Golden HoYeah') {
      return '/main/games/icons/goldenhoyeah.png';
    }
    if (gameName === 'Granblue Fantasy') {
      return '/main/games/icons/granbluefantasy.webp';
    }
    if (gameName === 'Grand Roleplay') {
      return '/main/games/icons/grandrp.jpg';
    }
    if (gameName === 'Grand Theft Auto 5') {
      return '/main/games/icons/grandtheftauto.jpg';
    }
    if (gameName === 'Grow a Garden') {
      return '/main/games/icons/growagarden.jpg';
    }
    if (gameName === 'Hay Day') {
      return '/main/games/icons/hayday.jpg';
    }
    if (gameName === 'Honkai Impact 3rd') {
      return '/main/games/icons/honkaimpact3rd.jpg';
    }
    if (gameName === 'Honkai: Star Rail') {
      return '/main/games/icons/honkaistarrail.jpg';
    }
    if (gameName === 'Houchi Shoujo') {
      return '/main/games/icons/houchishouju.jpeg';
    }
    if (gameName === 'Identity V') {
      return '/main/games/icons/identityv.jpg';
    }
    if (gameName === 'Jailbreak') {
      return '/main/games/icons/jailbreak.webp';
    }
    if (gameName === "JoJo's Bizarre Adventure: Ora Ora Overdrive") {
      return '/main/games/icons/jojoOraOra.webp';
    }
    if (gameName === 'Jujutsu Infinite') {
      return '/main/games/icons/jujutsuinfinite.jpeg';
    }
    if (gameName === 'Jujutsu Kaisen Phantom Parade') {
      return '/main/games/icons/JujutsuKaisenPhantomParade.jpeg';
    }
    if (gameName === 'King Legacy') {
      return '/main/games/icons/kinglegacy.jpg';
    }
    if (gameName === 'Knight Online') {
      return '/main/games/icons/knightonline.jpg';
    }
    if (gameName === 'Knives Out') {
      return '/main/games/icons/knivesout.jpg';
    }
    if (gameName === 'Last Island of Survival') {
      return '/main/games/icons/lastislandofsurvival.avif';
    }
    if (gameName === 'Last War: Survival') {
      return '/main/games/icons/lastwarsurvival.jpg';
    }
    if (gameName === 'League of Legends') {
      return '/main/games/icons/leagueoflegends.jpg';
    }
    if (gameName === 'League of Legends: Wild Rift') {
      return '/main/games/icons/leagueoflegendswildrift.jpg';
    }
    if (gameName === 'Livly Island') {
      return '/main/games/icons/livlyisland.png';
    }
    if (gameName === 'Lords Mobile') {
      return '/main/games/icons/lordsmobile.jpg';
    }
    if (gameName === 'Lost Ark') {
      return '/main/games/icons/lostark.jpg';
    }
    if (gameName === 'Madoka Magica Magia Exedra') {
      return '/main/games/icons/MadokaMagicaMagiaExedra.jpg';
    }
    if (gameName === 'Marvel Contest of Champions') {
      return '/main/games/icons/marvelcontestofchampions.jpg';
    }
    if (gameName === 'Marvel Rivals') {
      return '/main/games/icons/marvelrivals.jpg';
    }
    if (gameName === 'Metin2') {
      return '/main/games/icons/metin2.jpg';
    }
    if (gameName === 'Millennium War Aigis') {
      return '/main/games/icons/MillenniumWarAigis.webp';
    }
    if (gameName === 'Mobile Legends') {
      return '/main/games/icons/mobilelegends.webp';
    }
    if (gameName === 'Monster Hunter Now') {
      return '/main/games/icons/MonsterHunterNow.jpeg';
    }
    if (gameName === 'Monster Strike') {
      return '/main/games/icons/monsterstrike.jpg';
    }
    if (gameName === 'Netflix') {
      return '/main/games/icons/netflix.jpg';
    }
    if (gameName === 'New World') {
      return '/main/games/icons/newworld.jpg';
    }
    if (gameName === 'One Piece Bounty Rush') {
      return '/main/games/icons/OnePieceBountyRush.jpg';
    }
    if (gameName === 'One Piece Treasure Cruise') {
      return '/main/games/icons/OnePieceTreasureCruise.jpg';
    }
    if (gameName === 'Origin') {
      return '/main/games/icons/orgin.jpg';
    }
    if (gameName === 'Overwatch 2') {
      return '/main/games/icons/overwatch2.jpg';
    }
    if (gameName === 'Path of Exile') {
      return '/main/games/icons/pathofexile.jpg';
    }
    if (gameName === 'Path of Exile 2') {
      return '/main/games/icons/pathofexile2.webp';
    }
    if (gameName === 'Persona 5: The Phantom X') {
      return '/main/games/icons/Persona5ThePhantomX.jpg';
    }
    if (gameName === 'Plants vs Brainrots') {
      return '/main/games/icons/PlantsvsBrainrots.jpg';
    }
    if (gameName === 'PlayStation') {
      return '/main/games/icons/playstation.jpg';
    }
    if (gameName === 'Pokecolo') {
      return '/main/games/icons/pokecolo.jpg';
    }
    if (gameName === 'Pokecolo Twin') {
      return '/main/games/icons/pokecolotwin.jpg';
    }
    if (gameName === 'Pokemon Go') {
      return '/main/games/icons/pokemongo.jpg';
    }
    if (gameName === 'Pokemon TCG Pocket') {
      return '/main/games/icons/pokemontcgpocket.jpg';
    }
    if (gameName === 'Princess Connect! Re:Dive') {
      return '/main/games/icons/PrincessConnectRe-Dive.png';
    }
    if (gameName === 'Professional Baseball Spirits A') {
      return '/main/games/icons/ProfessionalBaseballSpiritsA.jpg';
    }
    if (gameName === 'Project SEKAI') {
      return '/main/games/icons/projectsekai.webp';
    }
    if (gameName === 'PUBG') {
      return '/main/games/icons/pubg.jpg';
    }
    if (gameName === 'PUBG Mobile') {
      return '/main/games/icons/pubgmobile.jpg';
    }
    if (gameName === 'Puzzle and Dragons') {
      return '/main/games/icons/PuzzleandDragons.webp';
    }
    if (gameName === 'Raid: Shadow Legends') {
      return '/main/games/icons/raidshadowlegends.jpg';
    }
    if (gameName === 'Rainbow Six Siege X') {
      return '/main/games/icons/RainbowSixSiegeX.jpg';
    }
    if (gameName === 'Rise of Kingdoms') {
      return '/main/games/icons/RiseofKingdoms.jpg';
    }
    if (gameName === 'Rocket League') {
      return '/main/games/icons/rocketleague.jpg';
    }
    if (gameName === 'Royale Online') {
      return '/main/games/icons/royaleonline.jpg';
    }
    if (gameName === 'RuneScape 3') {
      return '/main/games/icons/runescape3.jpg';
    }
    if (gameName === 'Rust') {
      return '/main/games/icons/rust.png';
    }
    if (gameName === 'SD Gundam G Generation ETERNAL') {
      return '/main/games/icons/eternal.webp';
    }
    if (gameName === 'Sea of Thieves') {
      return '/main/games/icons/seaofthieves.jpg';
    }
    if (gameName === 'Seven Knights Re:BIRTH') {
      return '/main/games/icons/SevenKnightsReBIRTH.jpg';
    }
    if (gameName === 'Shadowverse') {
      return '/main/games/icons/shadowverse.jpg';
    }
    if (gameName === 'Silver and Blood') {
      return '/main/games/icons/silverandblood.webp';
    }
    if (gameName === 'Sky: Children of the Light') {
      return '/main/games/icons/SkyChildrenoftheLight.jpg';
    }
    if (gameName === 'Spotify') {
      return '/main/games/icons/spotify.jpg';
    }
    if (gameName === 'Standoff 2') {
      return '/main/games/icons/standoff2.jpg';
    }
    if (gameName === 'Steal a Brainrot') {
      return '/main/games/icons/stealabrainrot.jpg';
    }
    if (gameName === 'Stella Sora') {
      return '/main/games/icons/stellasora.jpg';
    }
    if (gameName === 'Summoners War') {
      return '/main/games/icons/SummonersWar.jpg';
    }
    if (gameName === 'The Finals') {
      return '/main/games/icons/thefinals.jpg';
    }
    if (gameName === 'The Forge') {
      return '/main/games/icons/theforge.jpg';
    }
    if (gameName === 'The Seven Deadly Sins') {
      return '/main/games/icons/thesevendeadlysins.jpg';
    }
    if (gameName === 'Throne and Liberty') {
      return '/main/games/icons/ThroneandLiberty.jpg';
    }
    if (gameName === 'Tower of Fantasy') {
      return '/main/games/icons/toweroffantasy.jpg';
    }
    if (gameName === 'Ubisoft Connect') {
      return '/main/games/icons/ubisoftconnect.jpeg';
    }
    if (gameName === 'Uma Musume Pretty Derby') {
      return '/main/games/icons/UmaMusumePrettyDerby.jpg';
    }
    if (gameName === 'unVEIL the world') {
      return '/main/games/icons/unVEILtheworld.jpeg';
    }
    if (gameName === 'Volleyball Legends') {
      return '/main/games/icons/volleyballlegends.webp';
    }
    if (gameName === 'War Thunder') {
      return '/main/games/icons/warthunder.jpg';
    }
    if (gameName === 'Warframe') {
      return '/main/games/icons/warframe.jpg';
    }
    if (gameName === 'Wartune Ultra') {
      return '/main/games/icons/wartuneultra.png';
    }
    if (gameName === 'Watcher of Realms') {
      return '/main/games/icons/watcherofrealms.jpg';
    }
    if (gameName === 'Where Winds Meet') {
      return '/main/games/icons/WhereWindsMeet.jpg';
    }
    if (gameName === 'White Cat Project') {
      return '/main/games/icons/WhiteCatProject.jpg';
    }
    if (gameName === 'Whiteout Survival') {
      return '/main/games/icons/whiteoutsurvial.jpg';
    }
    if (gameName === 'WolfTeam') {
      return '/main/games/icons/wolfteam.jpg';
    }
    if (gameName === 'World of Tanks') {
      return '/main/games/icons/worldoftanks.jpg';
    }
    if (gameName === 'World of Warcraft') {
      return '/main/games/icons/worldofwarcraft.jpg';
    }
    if (gameName === 'World of Warships') {
      return '/main/games/icons/worldofwarships.jpg';
    }
    if (gameName === 'WoW Classic') {
      return '/main/games/icons/wowclassic.jpg';
    }
    if (gameName === 'WoW Mists of Pandaria') {
      return '/main/games/icons/WoWMistsofPandaria.jpg';
    }
    if (gameName === 'Wuthering Waves') {
      return '/main/games/icons/WutheringWaves.jpg';
    }
    if (gameName === 'Xbox') {
      return '/main/games/icons/xbox.jpg';
    }
    if (gameName === 'Yo-kai Watch: Wibble Wobble') {
      return '/main/games/icons/YokaiWatchWibbleWobble.jpg';
    }
    if (gameName === 'Yu-Gi-Oh! Master Duel') {
      return '/main/games/icons/Yu-Gi-OhMasterDuel.jpg';
    }
    if (gameName === 'Zenless Zone Zero') {
      return '/main/games/icons/ZenlessZoneZero.jpg';
    }
    if (gameName === 'Zula') {
      return '/main/games/icons/zula.jpeg';
    }
    if (gameName === 'Goddess of Victory: Nikke') {
      return '/main/games/icons/nikke.jpg';
    }
    
    return null;
  };

  // For boosting notifications, show game icon
  if (notification.type === 'booster_approved' || notification.type === 'booster_rejected' || notification.type === 'new_boosting_request' || notification.type === 'new_boosting_offer') {
    const game = notification.metadata?.game;
    if (game) {
      const gameIconPath = getGameIconPath(game);
      if (gameIconPath) {
        return (
          <div
            className="notification-icon-game"
            style={{ width: size, height: size }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <img
              src={gameIconPath}
              alt={game}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
              onError={(e) => {
                // Fallback to skeleton if image doesn't exist (no emoji flash)
                e.target.style.display = 'none';
                const parent = e.target.parentElement;
                if (parent) {
                  parent.style.background = 'var(--bg-quaternary)';
                  parent.style.borderRadius = '50%';
                  parent.style.animation = 'skeleton-loading 1.5s ease-in-out infinite';
                  parent.style.backgroundSize = '200% 100%';
                }
              }}
            />
          </div>
        );
      }
    }
  }

  // If no animation data and no animation path, show skeleton placeholder instead of emoji
  if (!animationData && animationPath === null) {
    return (
      <div
        className="notification-icon-default"
        style={{ 
          width: size, 
          height: size, 
          borderRadius: '50%',
          background: 'var(--bg-quaternary)',
          animation: 'skeleton-loading 1.5s ease-in-out infinite',
          backgroundSize: '200% 100%'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />
    );
  }

  // Show loading state while animation loads - use skeleton instead of emoji
  if (!animationData && animationPath) {
    return (
      <div
        className="notification-icon-loading"
        style={{ 
          width: size, 
          height: size, 
          borderRadius: '50%',
          background: 'var(--bg-quaternary)',
          animation: 'skeleton-loading 1.5s ease-in-out infinite',
          backgroundSize: '200% 100%'
        }}
      />
    );
  }

  return (
    <div
      className="notification-icon-lottie"
      style={{ width: size, height: size }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        if (lottieRef.current) {
          lottieRef.current.stop();
          lottieRef.current.goToAndStop(0);
        }
      }}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        autoplay={false}
        loop={false}
        style={{ width: size, height: size }}
      />
    </div>
  );
};

export default NotificationLottieIcon;

